import {
  TENANT_AT,
  TENANT_CH,
  TENANT_COM,
  TENANT_FR,
  TENANT_IT,
} from 'shared/consts'
import { PRICE_TYPES } from 'shared/consts/productTemplates'
import { type PageType } from 'types/cmsTypes'
import { type Tenant } from 'types/shopConfig'
import getImageUrl from 'views/components/organisms/AlgoliaInstantSearch/Algolia/getImageUrl'
import { calculateRetailPrice } from 'views/utils/esCompatibilityUtils'

import { EXCLUDED_PAGE_TYPES } from './consts'
import {
  type SearchHistorySuggestion,
  type SearchHit,
  type SearchIndexFetcher,
  type SearchSuggestion,
} from './types'

export const convertHitToSuggestion = ({
  cdnUrl,
  product,
  queryID,
  isRebrandingEnabled,
}: {
  cdnUrl: string
  product: SearchHit
  queryID?: string
  isRebrandingEnabled?: boolean
}): SearchSuggestion => {
  const {
    productName: name,
    pzn: id,
    price,
    averageRating,
    deeplink,
    variants,
    objectID,
  } = product

  let lowestPrice = Number(price)
  let newName = name
  let newDeepLink = deeplink

  variants?.forEach(({ retailPrice, productName, webDetailPath }) => {
    if (lowestPrice > Number(retailPrice)) {
      lowestPrice = Number(retailPrice)
      newName = productName
      newDeepLink = webDetailPath
    }
  })

  return {
    id,
    queryID,
    objectID,
    deeplink: newDeepLink,
    name: newName,
    averageRating,
    prices: [
      {
        amount: lowestPrice ? calculateRetailPrice(lowestPrice) : price,
        type: PRICE_TYPES.RETAIL,
      },
    ],
    thumbnailURL: getImageUrl({ product, cdnUrl, isRebrandingEnabled }),
  }
}

export const convertHitToHistorySuggestion = ({
  product,
  queryID,
}: {
  product: SearchHit
  queryID?: string
}): SearchHistorySuggestion => ({
  queryID,
  ...product,
})

export const getTopProducts: SearchIndexFetcher = (
  { index, deviceClass, expa },
  callback
) => {
  const ruleContexts: string[] = ['pqsl']
  if (expa) {
    ruleContexts.push(expa)
  }

  return index
    .search<SearchHit>('', {
      filters: 'stockStatusReason:product.stockStatus.available',
      hitsPerPage: 4,
      ruleContexts,
      analyticsTags: ['pqsl', deviceClass],
      clickAnalytics: true,
    })
    .then(callback)
}

export const getTopSearches: SearchIndexFetcher = (
  { index, deviceClass, expa = null },
  callback
) => {
  const ruleContexts: string[] = ['pqsl']
  if (expa) {
    ruleContexts.concat([expa])
  }

  return index
    .search<SearchHit>('', {
      hitsPerPage: 3,
      ruleContexts,
      analyticsTags: ['pqsl', deviceClass],
      clickAnalytics: true,
    })
    .then(callback)
}

export const shouldRenderPqsl = ({
  tenant,
  pageType,
  isPqslExperimentEnabled,
}: {
  tenant: Tenant
  pageType: PageType
  isPqslExperimentEnabled?: boolean
}): boolean => {
  if (EXCLUDED_PAGE_TYPES.includes(pageType)) {
    return false
  }

  switch (tenant) {
    case TENANT_COM:
      return true
    case TENANT_AT:
    case TENANT_CH:
    case TENANT_FR:
    case TENANT_IT:
      return Boolean(isPqslExperimentEnabled)
    default:
      return false
  }
}
